import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Card, Popconfirm } from 'antd';
import { getAllTenants, addTenant, updateTenant, deleteTenant } from '../../redux/actions/tenants';
import CustomInput from '../../common/CustomInput';
import CustomModal from '../../common/CustomModal';

function TenantForm({ initial = {}, onSave, onCancel, isEdit }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    phoneNumber: initial.phoneNumbers?.[0] || initial.phoneNumber || '',
  });

  const handleSave = () => {
    if (!form.name?.trim()) return;
    onSave({ name: form.name, phoneNumbers: [form.phoneNumber] });
  };

  return (
    <div>
      <CustomInput
        label="Tenant Name"
        placeholder="Enter tenant name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required
        requiredMessage="Tenant name is required"
      />
      <CustomInput
        label="Phone Number"
        placeholder="Enter phone number"
        value={form.phoneNumber}
        onChange={(e) =>
          setForm({ ...form, phoneNumber: e.target.value.replace(/\D/g, '') })
        }
        maxLength={10}
        validation={{
          pattern: /^\d{10}$/,
          message: 'Phone number must be exactly 10 digits',
        }}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" onClick={handleSave}>
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </div>
    </div>
  );
}

function TenantsManagement() {
  const dispatch = useDispatch();
  const tenants = useSelector((state) => state.tenants?.data || []);
  const isLoading = useSelector((state) => state.tenants?.isProcessing);

  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    dispatch(getAllTenants());
  }, [dispatch]);

  const handleAdd = (form) => {
    dispatch(addTenant(form)).then(() => {
      dispatch(getAllTenants());
      setModalVisible(false);
    });
  };

  const handleEdit = (form) => {
    dispatch(updateTenant(selectedTenant.id, { ...selectedTenant, ...form })).then(() => {
      dispatch(getAllTenants());
      setModalVisible(false);
      setSelectedTenant(null);
    });
  };

  const handleDelete = (id) => {
    dispatch(deleteTenant(id)).then(() => {
      dispatch(getAllTenants());
    });
  };

  const openEdit = (record) => {
    setSelectedTenant(record);
    setModalVisible(true);
  };

  const openAdd = () => {
    setSelectedTenant(null);
    setModalVisible(true);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumbers',
      key: 'phoneNumbers',
      render: (phoneNumbers) => phoneNumbers?.[0] || '-',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <>
          <Button type="primary" onClick={() => openEdit(record)} style={{ marginRight: 8 }}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this tenant?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Add Tenant"
        bordered
        style={{ border: '2px solid rgb(0, 168, 204)', marginBottom: 24 }}
        styles={{
          header: {
            borderBottom: '2px solid rgb(0, 168, 204)',
            fontSize: '2rem',
            textAlign: 'center',
            color: 'rgb(0, 168, 204)',
          },
        }}
      >
        <Button type="primary" onClick={openAdd}>
          + Add Tenant
        </Button>
      </Card>

      <h1>List of Tenants</h1>
      {isLoading ? <p>Loading...</p> : null}
      <Table
        dataSource={tenants.map((t) => ({ ...t, key: t.id }))}
        columns={columns}
        rowKey="id"
        locale={{ emptyText: 'No Tenants Found' }}
      />

      {isModalVisible && (
        <CustomModal
          visible={isModalVisible}
          onClose={() => {
            setModalVisible(false);
            setSelectedTenant(null);
          }}
          title={selectedTenant ? 'Edit Tenant' : 'Add Tenant'}
          content={
            <TenantForm
              initial={selectedTenant || {}}
              isEdit={!!selectedTenant}
              onSave={selectedTenant ? handleEdit : handleAdd}
              onCancel={() => {
                setModalVisible(false);
                setSelectedTenant(null);
              }}
            />
          }
        />
      )}
    </>
  );
}

export default TenantsManagement;
